import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { CheckIcon, ChevronDownIcon, CloseIcon, SearchIcon, SlidersIcon } from '@/components/icons';
import { Input, Pill, cx } from '@/components/ui';

/**
 * The filter bar every admin list shares.
 *
 * One shape, so an operator who learns Orders already knows Products. Three
 * things it insists on, because a list screen is unusable without them:
 *
 *  - **A result count.** "12 of 340" is the difference between a filter that
 *    worked and one that emptied the screen; without it an empty table looks
 *    like a broken query.
 *  - **Active filters shown as removable chips.** A filter you cannot see is a
 *    filter you will forget you set, and then report as missing data.
 *  - **Advanced filters folded away.** The common case is a search box; the
 *    date and amount ranges are there when wanted and out of the way when not.
 */
export function FilterBar({
  search,
  onSearch,
  placeholder,
  chips,
  onClearAll,
  total,
  shown,
  children,
}: {
  search: string;
  onSearch: (value: string) => void;
  placeholder?: string;
  /** One per active filter, so each can be dropped on its own. */
  chips?: { key: string; label: string; onRemove: () => void }[];
  onClearAll?: () => void;
  total?: number;
  shown?: number;
  /** The advanced fields, revealed on demand. */
  children?: ReactNode;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(search);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced, so a list does not refetch on every keystroke.
  useEffect(() => {
    if (draft === search) return;
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => onSearch(draft), 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [draft, search, onSearch]);

  // An external reset — "clear all" — has to reach the input too.
  useEffect(() => {
    setDraft(search);
  }, [search]);

  const active = chips ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-56 flex-1">
          <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
            <SearchIcon className="size-4" />
          </span>
          <Input
            type="search"
            value={draft}
            placeholder={placeholder ?? t('searchLabel')}
            onChange={(event) => setDraft(event.target.value)}
            className="ps-9 pe-9 [&::-webkit-search-cancel-button]:hidden"
          />
          {/* Our own clear: the browser's differs per platform and ignores RTL. */}
          {draft ? (
            <button
              type="button"
              onClick={() => {
                setDraft('');
                onSearch('');
              }}
              aria-label={t('clearSearch')}
              title={t('clearSearch')}
              className="absolute inset-y-0 end-1.5 my-auto inline-flex size-7 items-center justify-center rounded-md text-ink-faint transition-colors hover:bg-ink/[0.06] hover:text-ink"
            >
              <CloseIcon className="size-3.5" />
            </button>
          ) : null}
        </div>

        {children ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
            className={cx(
              'inline-flex min-h-10 shrink-0 items-center gap-2 rounded-control border px-3.5 text-sm font-medium whitespace-nowrap transition-colors',
              open || active.length > 0
                ? 'border-brand-500/40 bg-brand-50 text-brand-700'
                : 'border-line-strong bg-surface text-ink shadow-card hover:bg-surface-sunken',
            )}
          >
            <SlidersIcon className="size-4" />
            {t('advancedFilters')}
            {active.length > 0 ? (
              <span data-numeric className="inline-flex min-w-5 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] leading-5 font-semibold text-brand-ink">
                {count(active.length, locale)}
              </span>
            ) : null}
            <ChevronDownIcon className={cx('size-4 opacity-60 transition-transform', open && 'rotate-180')} />
          </button>
        ) : null}

        {/* Stated plainly, and only once a filter is doing something. */}
        {total !== undefined ? (
          <span data-numeric className="ms-auto shrink-0 text-[13px] text-ink-muted">
            {shown !== undefined && shown !== total
              ? t('showingOf', { shown: count(shown, locale), total: count(total, locale) })
              : t('resultCount', { total: count(total, locale) })}
          </span>
        ) : null}
      </div>

      {open && children ? (
        <div className="animate-pop rounded-panel border border-line bg-surface-sunken/70 p-4 sm:p-5">{children}</div>
      ) : null}

      {active.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {active.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex h-7 items-center gap-1 rounded-full bg-brand-50 ps-3 pe-1 text-xs font-medium text-brand-700 ring-1 ring-brand-500/25 ring-inset"
            >
              {chip.label}
              <button
                type="button"
                onClick={chip.onRemove}
                aria-label={`${t('remove')} — ${chip.label}`}
                className="inline-flex size-5 items-center justify-center rounded-full opacity-70 transition hover:bg-brand-500/15 hover:opacity-100"
              >
                <CloseIcon className="size-3" />
              </button>
            </span>
          ))}

          {onClearAll ? (
            <button
              type="button"
              onClick={onClearAll}
              className="rounded-md px-1.5 py-1 text-xs font-medium text-ink-muted transition-colors hover:text-danger"
            >
              {t('clearFilters')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A labelled cell inside the advanced panel. */
export function FilterField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="flex min-w-0 flex-col gap-1.5">
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      {children}
    </label>
  );
}

/**
 * Several values from a fixed set, as toggles.
 *
 * A multi-select `<select>` is unusable on a touchscreen and invisible about
 * what is chosen; a row of toggles says it at a glance.
 */
export function ToggleGroup({
  options,
  selected,
  onChange,
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((option) => {
        const isOn = selected.includes(option.value);

        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={isOn}
            onClick={() =>
              onChange(
                isOn
                  ? selected.filter((value) => value !== option.value)
                  : [...selected, option.value],
              )
            }
            className={cx(
              'inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-medium transition-colors',
              isOn
                ? 'border-brand-500/50 bg-brand-50 text-brand-700'
                : 'border-line-strong bg-surface text-ink-muted hover:border-ink-faint/60 hover:text-ink',
            )}
          >
            {/* The tick says "on" without leaning on colour alone. */}
            {isOn ? <CheckIcon className="-ms-0.5 size-3.5" /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export { Pill };
